import { useEffect, useState } from "react";
import { useClientProjectsStore } from "../../../store/useClientProjectsStore";
import {
  MIcon,
  ModalShell,
  StatusChip,
  HealthChip,
  EmptyState,
  InvoiceChip,
  fmtDate,
  money,
  stageIcon,
  actionIcon,
  PRIORITY_TONE,
} from "./shared";

/**
 * The complete client-facing record for one project.
 *
 * Everything shown here was selected server-side from a fixed list of
 * client-safe fields. Internal notes, tasks, costs, margins and team
 * commentary are never fetched, so there is nothing here to accidentally
 * reveal.
 */

const Section = ({
  icon,
  title,
  meta,
  children,
}: {
  icon: string;
  title: string;
  meta?: string;
  children: React.ReactNode;
}) => (
  <section className="mb-lg last:mb-0">
    <div className="flex items-center gap-2 mb-sm">
      <MIcon name={icon} className="text-primary text-[18px]" />
      <h3 className="text-[12px] font-bold uppercase tracking-wider text-on-surface">{title}</h3>
      {meta && <span className="text-[11px] text-on-surface-variant font-medium">· {meta}</span>}
    </div>
    {children}
  </section>
);

const Fact = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <p className="text-[11px] uppercase font-bold tracking-wider text-tertiary">{label}</p>
    <p className="text-body-sm text-on-surface font-medium mt-0.5">{value}</p>
  </div>
);

const MILESTONE_TONE: Record<string, string> = {
  Completed: "bg-tertiary-container text-tertiary",
  "In Progress": "bg-primary/10 text-primary",
  Pending: "bg-surface-container text-on-surface-variant",
};

const ProjectDetailsModal = ({
  projectId,
  onClose,
  onViewLifecycle,
}: {
  projectId: string | null;
  onClose: () => void;
  onViewLifecycle: (id: string) => void;
}) => {
  const { fetchProject, completeAction } = useClientProjectsStore();
  const [p, setP] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  const load = async (id: string) => {
    setLoading(true);
    const data = await fetchProject(id);
    setP(data);
    setLoading(false);
  };

  useEffect(() => {
    if (!projectId) {
      setP(null);
      return;
    }
    load(projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const onComplete = async (actionId: string) => {
    setBusyAction(actionId);
    const ok = await completeAction(actionId);
    setBusyAction(null);
    if (ok && projectId) load(projectId);
  };

  return (
    <ModalShell
      open={!!projectId}
      onClose={onClose}
      icon="work"
      title={p?.name || "Project"}
      subtitle={p ? [p.category, p.reference && `#${p.reference}`].filter(Boolean).join(" · ") : ""}
      width="max-w-5xl"
    >
      {loading && !p ? (
        <div className="py-xl text-center text-body-sm text-on-surface-variant">Loading this project…</div>
      ) : !p ? (
        <EmptyState title="Project unavailable" detail="Please try again in a moment." />
      ) : (
        <>
          {/* Header facts */}
          <div className="p-md rounded-xl bg-surface-container-low border border-outline-variant/40 mb-lg">
            <div className="flex items-center gap-2 flex-wrap mb-md">
              <StatusChip status={p.status} />
              <HealthChip health={p.health} />
              <span className="inline-flex items-center gap-1.5 text-body-sm text-secondary font-semibold">
                <MIcon name={stageIcon(p.currentStage)} className="text-[16px]" />
                {p.currentStage || "—"}
              </span>
              <button
                onClick={() => onViewLifecycle(p.id)}
                className="text-[11px] text-primary hover:underline font-medium inline-flex items-center gap-0.5"
              >
                View Lifecycle
                <MIcon name="arrow_forward" className="text-[12px]" />
              </button>
            </div>

            <div className="flex items-center gap-2 mb-md">
              <div className="flex-1 h-2 bg-surface-container rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: `${p.progress}%` }} />
              </div>
              <span className="text-body-sm font-bold text-on-surface">{p.progress}%</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-md">
              <Fact label="Started" value={fmtDate(p.startDate)} />
              <Fact label="Expected" value={fmtDate(p.expectedCompletion)} />
              <Fact label="Completed" value={p.actualCompletion ? fmtDate(p.actualCompletion) : "—"} />
              <Fact label="Next stage" value={p.nextStage || "—"} />
            </div>
          </div>

          {/* AI project status */}
          <Section icon="auto_awesome" title="AI Project Status">
            <div className="p-md rounded-xl bg-primary/5 border border-primary/20">
              <p className="text-body-sm text-on-surface leading-relaxed">{p.aiStatus}</p>
            </div>
          </Section>

          {p.description && (
            <Section icon="description" title="Project Information">
              <p className="text-body-sm text-on-surface-variant leading-relaxed whitespace-pre-line">
                {p.description}
              </p>
            </Section>
          )}

          {/* Client actions */}
          <Section icon="flag" title="Your Actions">
            {!p.clientActions?.length ? (
              <EmptyState
                icon="task_alt"
                title="Nothing needs you on this project"
                detail="We will let you know as soon as something does."
              />
            ) : (
              <div className="space-y-sm">
                {p.clientActions.map((a: any) => {
                  const isInvoice = a.type === "Make Payment";
                  const done = a.status === "Completed";
                  return (
                    <div
                      key={a.id}
                      className={`p-md rounded-xl border flex items-start justify-between gap-3 flex-wrap ${
                        done ? "border-outline-variant bg-surface-container-low/40" : "border-outline-variant"
                      }`}
                    >
                      <div className="flex items-start gap-2.5 min-w-0">
                        <MIcon
                          name={done ? "check_circle" : actionIcon(a.type)}
                          className={`text-[18px] mt-0.5 ${done ? "text-tertiary" : "text-primary"}`}
                        />
                        <div className="min-w-0">
                          <p
                            className={`text-body-sm font-bold ${
                              done ? "text-on-surface-variant line-through" : "text-on-surface"
                            }`}
                          >
                            {a.title}
                          </p>
                          {a.detail && (
                            <p className="text-[11px] text-on-surface-variant mt-0.5">{a.detail}</p>
                          )}
                          <p className="text-[11px] text-on-surface-variant mt-0.5">
                            {a.type}
                            {a.dueDate ? ` · Due ${fmtDate(a.dueDate)}` : ""}
                            {isInvoice && a.amount ? ` · ${money(a.amount, a.currency)}` : ""}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!done && (
                          <span
                            className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${
                              PRIORITY_TONE[a.priority] || PRIORITY_TONE.Required
                            }`}
                          >
                            {a.priority}
                          </span>
                        )}
                        {done ? (
                          <span className="text-[11px] font-bold text-tertiary">Done</span>
                        ) : isInvoice ? (
                          <a
                            href="/portal/invoices"
                            className="px-md py-1.5 rounded-lg bg-primary text-on-primary text-[12px] font-bold hover:bg-primary/90 transition-colors whitespace-nowrap"
                          >
                            View Invoice
                          </a>
                        ) : (
                          <button
                            onClick={() => onComplete(a.id)}
                            disabled={busyAction === a.id}
                            className="px-md py-1.5 rounded-lg bg-primary text-on-primary text-[12px] font-bold hover:bg-primary/90 transition-colors disabled:opacity-60 whitespace-nowrap"
                          >
                            {busyAction === a.id ? "Saving…" : a.type === "Approve" ? "Approve" : "Mark done"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Delivery milestones */}
          <Section icon="flag_circle" title="Milestones">
            {!p.milestones?.length ? (
              <EmptyState icon="flag" title="No milestones yet" detail="They will appear here once the plan is set." />
            ) : (
              <div className="overflow-x-auto rounded-xl border border-outline-variant">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-surface-container-low">
                    <tr>
                      {["Milestone", "Status", "Due Date", "Progress"].map((h) => (
                        <th key={h} className="px-md py-sm text-label-md text-tertiary uppercase">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant">
                    {p.milestones.map((m: any) => (
                      <tr key={m.id}>
                        <td className="px-md py-sm">
                          <p className="text-body-sm font-semibold text-on-surface">{m.title}</p>
                          {m.deliverables?.length > 0 && (
                            <p className="text-[11px] text-on-surface-variant mt-0.5">
                              {m.deliverables.filter((d: any) => d.done).length} of {m.deliverables.length}{" "}
                              deliverables ready
                            </p>
                          )}
                        </td>
                        <td className="px-md py-sm">
                          <span
                            className={`px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap ${
                              MILESTONE_TONE[m.status] || MILESTONE_TONE.Pending
                            }`}
                          >
                            {m.status}
                          </span>
                        </td>
                        <td className="px-md py-sm text-body-sm text-on-surface-variant whitespace-nowrap">
                          {fmtDate(m.dueDate)}
                        </td>
                        <td className="px-md py-sm">
                          <div className="flex items-center gap-2 min-w-[100px]">
                            <div className="flex-1 h-1.5 bg-surface-container rounded-full overflow-hidden">
                              <div className="h-full bg-primary rounded-full" style={{ width: `${m.progress}%` }} />
                            </div>
                            <span className="text-[11px] font-bold text-on-surface">{m.progress}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Deliverables */}
          {p.deliverables?.length > 0 && (
            <Section icon="inventory_2" title="Deliverables">
              <div className="overflow-x-auto rounded-xl border border-outline-variant">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-surface-container-low">
                    <tr>
                      {["Deliverable", "Status", "Review", "Action"].map((h) => (
                        <th key={h} className="px-md py-sm text-label-md text-tertiary uppercase">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant">
                    {p.deliverables.map((d: any, i: number) => (
                      <tr key={i}>
                        <td className="px-md py-sm">
                          <p className="text-body-sm font-semibold text-on-surface">{d.name}</p>
                          <p className="text-[11px] text-on-surface-variant">{d.milestone}</p>
                        </td>
                        <td className="px-md py-sm text-body-sm text-on-surface-variant">{d.status}</td>
                        <td className="px-md py-sm text-body-sm text-on-surface-variant">{d.review}</td>
                        <td className="px-md py-sm">
                          {d.action ? (
                            <button
                              onClick={() => d.actionId && onComplete(d.actionId)}
                              className="text-[12px] font-bold text-primary hover:underline"
                            >
                              {d.action}
                            </button>
                          ) : (
                            <span className="text-body-sm text-on-surface-variant">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* Payment milestones + invoices */}
          {(p.paymentMilestones?.length > 0 || p.invoices?.length > 0) && (
            <Section icon="payments" title="Payments">
              {p.paymentMilestones?.length > 0 && (
                <ul className="space-y-1.5 mb-sm">
                  {p.paymentMilestones.map((m: any) => (
                    <li
                      key={m.id}
                      className="flex items-center justify-between gap-2 text-body-sm p-2.5 rounded-lg bg-surface-container-low/50"
                    >
                      <span className="text-on-surface font-medium">{m.title}</span>
                      <span className="flex items-center gap-3">
                        <span className="text-on-surface-variant">{m.status}</span>
                        <b className="text-on-surface">{money(m.amount)}</b>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {p.invoices?.length > 0 && (
                <ul className="space-y-1.5">
                  {p.invoices.map((i: any) => (
                    <li
                      key={i.id}
                      className={`flex items-center justify-between gap-2 text-body-sm p-2.5 rounded-lg border ${
                        i.blocking ? "border-error/30 bg-error-container/20" : "border-outline-variant"
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="text-on-surface font-medium">{i.number}</span>
                        {/* Said only when the workflow confirms the dependency —
                            an unpaid invoice is not by itself a blocker. */}
                        {i.blocking && (
                          <span className="block text-[11px] font-semibold text-error mt-0.5">
                            The next stage cannot begin until this is paid.
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-on-surface-variant">
                          {i.due ? `Due ${fmtDate(i.due)}` : ""}
                        </span>
                        <InvoiceChip status={i.status} />
                        <b className="text-on-surface">{money(i.amount, i.currency)}</b>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          )}

          {/* Activity */}
          {p.activity?.length > 0 && (
            <Section icon="history" title="Recent Activity">
              <ul className="space-y-2">
                {p.activity.map((a: any, i: number) => (
                  <li key={i} className="flex items-start gap-2.5 text-body-sm">
                    <MIcon
                      name={a.type.includes("completed") ? "check_circle" : "notifications"}
                      className={`text-[16px] mt-0.5 ${
                        a.type.includes("completed") ? "text-tertiary" : "text-primary"
                      }`}
                    />
                    <span className="text-on-surface">
                      {a.title}
                      <span className="text-on-surface-variant"> · {fmtDate(a.at)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Files */}
          <Section icon="folder" title="Project Files">
            {!p.files?.length ? (
              <EmptyState icon="folder_open" title="No files shared yet" />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-sm">
                {p.files.map((f: any, i: number) => (
                  <a
                    key={i}
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    className="p-md rounded-xl border border-outline-variant hover:border-primary/40 hover:bg-surface-container-low transition-colors flex items-center gap-3"
                  >
                    <MIcon name="draft" className="text-primary text-[20px]" />
                    <span className="min-w-0">
                      <span className="block text-body-sm font-semibold text-on-surface truncate">
                        {f.name}
                      </span>
                      <span className="block text-[11px] text-on-surface-variant">
                        {f.category} · {fmtDate(f.uploadedAt)}
                      </span>
                    </span>
                  </a>
                ))}
              </div>
            )}
          </Section>
        </>
      )}
    </ModalShell>
  );
};

export default ProjectDetailsModal;
